import type { ECharts } from 'echarts'
import { getRulerPlotRect } from './chartRulerGeometry'
import { draggedValueExtent } from './chartScale'

/** Axis scaling stays local even when ECharts links the panels' horizontal zoom. */
export function attachValueAxisDrag(chart: ECharts, onManualScale: () => void, onAxisClick: () => void) {
  const dom = chart.getDom()
  let drag: { origin: number; min: number; max: number; height: number; moved: boolean; distance: number;
    zooms: Array<Record<string, unknown>> } | null = null
  let frame = 0
  let clickTimer = 0
  let suppressClick = false
  let previousCursor = ''
  let cursorActive = false
  const cursor = (active: boolean) => {
    if (active && !cursorActive) previousCursor = dom.style.cursor
    if (active || cursorActive) {
      const next = active ? 'ns-resize' : dom.style.cursor === 'crosshair' ? 'crosshair' : previousCursor
      dom.style.cursor = next
      if (!chart.isDisposed()) chart.getZr().setCursorStyle(next)
    }
    cursorActive = active
  }
  const suppress = (event: Event) => { event.preventDefault(); event.stopImmediatePropagation() }
  const point = (event: MouseEvent) => {
    const rect = dom.getBoundingClientRect()
    return {
      x: (event.clientX - rect.left) * chart.getWidth() / (rect.width || 1),
      y: (event.clientY - rect.top) * chart.getHeight() / (rect.height || 1),
    }
  }
  const paint = () => {
    frame = 0
    if (!drag || !drag.moved || chart.isDisposed()) return
    const extent = draggedValueExtent(drag.min, drag.max, drag.distance, drag.height)
    if (extent) chart.setOption({ yAxis: extent, dataZoom: drag.zooms }, { silent: true })
  }
  const finish = () => {
    if (!drag) return
    cancelAnimationFrame(frame)
    frame = 0
    drag = null
    cursor(false)
    window.clearTimeout(clickTimer)
    clickTimer = window.setTimeout(() => { suppressClick = false }, 0)
  }
  const down = (event: MouseEvent) => {
    if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) cursor(false)
    if (chart.isDisposed() || event.defaultPrevented || event.button !== 0 || drag
      || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey || dom.style.cursor === 'crosshair') return
    const plot = getRulerPlotRect(chart)
    if (!plot) return
    const pixel = point(event)
    if (pixel.x <= plot.x + plot.width || pixel.x > chart.getWidth()
      || pixel.y < plot.y || pixel.y > plot.y + plot.height) return
    const top = chart.convertFromPixel({ gridIndex: 0 }, [plot.x, plot.y])
    const bottom = chart.convertFromPixel({ gridIndex: 0 }, [plot.x, plot.y + plot.height])
    if (!Array.isArray(top) || !Array.isArray(bottom)) return
    const min = Math.min(top[1], bottom[1])
    const max = Math.max(top[1], bottom[1])
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return
    const zooms = chart.getOption().dataZoom as Array<{ yAxisIndex?: unknown }> || []
    drag = { origin: event.clientY, min, max, height: plot.height, moved: false, distance: 0,
      zooms: zooms.map((zoom) => zoom.yAxisIndex != null
        ? { start: 0, end: 100, startValue: null, endValue: null } : {}),
    }
    cursor(true)
    suppressClick = true
    suppress(event)
  }
  const move = (event: MouseEvent) => {
    if (!drag && (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey)) cursor(false)
    if (!drag || chart.isDisposed()) return
    suppress(event)
    const rect = dom.getBoundingClientRect()
    drag.distance = (event.clientY - drag.origin) * chart.getHeight() / (rect.height || 1)
    if (!drag.moved && Math.abs(event.clientY - drag.origin) >= 3) {
      drag.moved = true
      onManualScale()
    }
    if (drag.moved && !frame) frame = requestAnimationFrame(paint)
  }
  const up = (event: MouseEvent) => {
    if (!drag || event.button !== 0) return
    if (chart.isDisposed()) { finish(); return }
    move(event)
    cancelAnimationFrame(frame)
    paint()
    const clicked = !drag.moved
    finish()
    if (clicked) onAxisClick()
  }
  const click = (event: MouseEvent) => { if (suppressClick) suppress(event) }
  const compatibility = (event: MouseEvent) => { if (drag || suppressClick) suppress(event) }
  const hover = (event: MouseEvent) => {
    if (drag || chart.isDisposed()) return
    if (event.defaultPrevented || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey || dom.style.cursor === 'crosshair') {
      cursor(false)
      return
    }
    const plot = getRulerPlotRect(chart)
    const pixel = point(event)
    cursor(!!plot && pixel.x > plot.x + plot.width && pixel.x <= chart.getWidth()
      && pixel.y >= plot.y && pixel.y <= plot.y + plot.height)
  }
  const leave = () => { if (!drag) cursor(false) }
  const cancel = () => { finish(); cursor(false) }
  const key = (event: KeyboardEvent) => { if (event.key === 'Escape') finish() }
  const pointer = typeof PointerEvent !== 'undefined'
  const downName = pointer ? 'pointerdown' : 'mousedown'
  const moveName = pointer ? 'pointermove' : 'mousemove'
  const upName = pointer ? 'pointerup' : 'mouseup'
  dom.addEventListener(downName, down, true)
  dom.addEventListener('click', click, true)
  // Bubble after ECharts' own hover handler and the ruler's capture handlers.
  dom.addEventListener(moveName, hover)
  dom.addEventListener('pointerleave', leave)
  dom.addEventListener('mouseleave', leave)
  if (pointer) for (const name of ['mousedown', 'mousemove', 'mouseup'] as const) dom.addEventListener(name, compatibility, true)
  window.addEventListener(moveName, move, true)
  window.addEventListener(upName, up, true)
  window.addEventListener('blur', cancel)
  window.addEventListener('pointercancel', cancel)
  window.addEventListener('keydown', key)
  const dispose = () => {
    cancel()
    window.clearTimeout(clickTimer)
    dom.removeEventListener(downName, down, true)
    dom.removeEventListener('click', click, true)
    dom.removeEventListener(moveName, hover)
    dom.removeEventListener('pointerleave', leave)
    dom.removeEventListener('mouseleave', leave)
    if (pointer) for (const name of ['mousedown', 'mousemove', 'mouseup'] as const) dom.removeEventListener(name, compatibility, true)
    window.removeEventListener(moveName, move, true)
    window.removeEventListener(upName, up, true)
    window.removeEventListener('blur', cancel)
    window.removeEventListener('pointercancel', cancel)
    window.removeEventListener('keydown', key)
  }
  return { dispose, cancel: finish }
}
